import { useEffect, useMemo, useState } from "react";

import { TRANSITION_CATEGORIES } from "../../lib/transitions";
import { DYNAMIC_TRANSITIONS } from "../../preview/transitionLibrary";
import TransitionThumb from "./TransitionThumb";

/** MIME del payload de arrastre interno desde el panel a la timeline. */
export const TRANSITION_DRAG_MIME = "application/x-videotool-transition";

interface TransitionEntry {
  id: string;
  label: string;
  category: string;
  icon?: string;
  glsl?: string;        // sólo para dinámicas
}

/** Catálogo unificado para el panel: dinámicas (con preview) + xfade (sin preview). */
function buildCatalog(): TransitionEntry[] {
  const out: TransitionEntry[] = [];
  // Dinámicas (con GLSL → preview real)
  for (const t of DYNAMIC_TRANSITIONS) {
    out.push({ id: t.id, label: t.label, category: t.category, icon: t.icon, glsl: t.glsl });
  }
  // Xfade builtin del shader compartido (sin preview pero exportables nativas)
  for (const cat of TRANSITION_CATEGORIES) {
    for (const item of cat.items) {
      if (out.find((x) => x.id === item.id)) continue;  // dedupe
      out.push({ id: item.id, label: item.label, category: cat.name, icon: item.icon });
    }
  }
  return out;
}

const CATALOG = buildCatalog();

export default function TransitionsPanel(props: {
  onClose: () => void;
  /** Si se pasa, click selecciona la transición y cierra; sino sólo arrastrable. */
  onPick?: (id: string) => void;
  /** "sidebar" = panel a la izquierda (default, permite drag al timeline);
   *  "modal" = centrado oscureciendo el resto (sólo recomendable para onPick). */
  variant?: "sidebar" | "modal";
}) {
  const { onClose, onPick, variant = "sidebar" } = props;
  const [query, setQuery] = useState("");

  // ESC cierra el panel
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") onClose();
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  // Agrupar por categoría preservando orden de aparición
  const groups = useMemo(() => {
    const filtered = CATALOG.filter((t) => {
      if (!query) return true;
      const q = query.toLowerCase();
      return t.label.toLowerCase().includes(q) || t.id.toLowerCase().includes(q);
    });
    const map = new Map<string, TransitionEntry[]>();
    for (const t of filtered) {
      if (!map.has(t.category)) map.set(t.category, []);
      map.get(t.category)!.push(t);
    }
    return Array.from(map.entries());
  }, [query]);

  function onDragStart(e: React.DragEvent, t: TransitionEntry) {
    e.dataTransfer.effectAllowed = "copy";
    e.dataTransfer.setData(TRANSITION_DRAG_MIME, JSON.stringify({ id: t.id }));
    e.dataTransfer.setData(`${TRANSITION_DRAG_MIME}+id`, t.id);  // detectable en dragover
  }

  // Modal centrado (cubre pantalla) sólo cuando se invoca como selector exclusivo.
  // Por defecto: panel lateral izquierdo, NO bloquea el timeline (drag&drop funciona).
  const isModal = variant === "modal";

  const wrapperStyle: React.CSSProperties = isModal ? {
    position: "fixed", inset: 0,
    display: "grid", placeItems: "center",
    background: "rgba(0,0,0,0.55)",
    zIndex: 9999,
  } : {
    position: "fixed",
    left: 0, top: 56, bottom: 0,
    width: 420,
    zIndex: 9000,
    pointerEvents: "none",  // sólo el panel interno captura eventos
  };

  const panelStyle: React.CSSProperties = isModal ? {
    width: "min(960px, 92vw)",
    maxHeight: "88vh",
    background: "var(--bg-elev)",
    border: "1px solid var(--border)",
    borderRadius: 10,
    padding: 16,
    boxShadow: "0 24px 80px rgba(0,0,0,0.6)",
    overflow: "hidden",
    display: "flex",
    flexDirection: "column",
  } : {
    width: "100%",
    height: "100%",
    background: "var(--bg-elev)",
    border: "1px solid var(--border)",
    borderRight: "1px solid var(--border)",
    boxShadow: "8px 0 30px rgba(0,0,0,0.4)",
    padding: 14,
    overflow: "hidden",
    display: "flex",
    flexDirection: "column",
    pointerEvents: "auto",
  };

  return (
    <div style={wrapperStyle} onClick={isModal ? onClose : undefined}>
      <div
        onClick={(e) => e.stopPropagation()}
        style={panelStyle}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 10 }}>
          <h3 style={{ margin: 0, fontSize: 16 }}>
            Transiciones <span style={{ opacity: 0.5, fontSize: 12 }}>({CATALOG.length})</span>
          </h3>
          <input
            placeholder="Buscar transición…"
            value={query} onChange={(e) => setQuery(e.target.value)}
            style={{ flex: 1, maxWidth: 280 }}
            autoFocus
          />
          <span style={{ flex: 1 }} />
          <span style={{ fontSize: 11, color: "var(--text-dim)" }}>
            Arrastra al borde de un clip · o haz click en el "+" entre dos clips
          </span>
          <button onClick={onClose} style={{ fontSize: 12 }}>×</button>
        </div>

        <div style={{ overflowY: "auto", flex: 1 }}>
          {groups.length === 0 && (
            <p style={{ color: "var(--text-dim)", textAlign: "center", padding: 20 }}>
              Sin resultados para "{query}".
            </p>
          )}
          {groups.map(([catName, items]) => (
            <section key={catName} style={{ marginBottom: 14 }}>
              <h4 style={{ margin: "0 0 6px", fontSize: 12, color: "var(--text-dim)",
                            textTransform: "uppercase", letterSpacing: 0.05 }}>
                {catName}
              </h4>
              <div style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fill, minmax(80px, 1fr))",
                gap: 8,
              }}>
                {items.map((t) => (
                  t.glsl ? (
                    <TransitionThumb
                      key={t.id}
                      id={t.id}
                      glslBody={t.glsl}
                      label={t.label}
                      icon={t.icon}
                      size={70}
                      draggable
                      onDragStart={(e) => onDragStart(e, t)}
                      onPick={onPick ? () => { onPick(t.id); onClose(); } : undefined}
                    />
                  ) : (
                    <div
                      key={t.id}
                      draggable
                      onDragStart={(e) => onDragStart(e, t)}
                      onClick={onPick ? () => { onPick(t.id); onClose(); } : undefined}
                      title={t.label}
                      style={{
                        display: "flex", flexDirection: "column", alignItems: "center",
                        gap: 4, padding: 6, borderRadius: 6,
                        cursor: onPick ? "pointer" : "grab",
                        background: "var(--bg-elev-2)",
                        border: "1px solid var(--border)",
                        minHeight: 64,
                      }}
                    >
                      <div style={{
                        width: 70, height: 40,
                        background: "linear-gradient(90deg, #c54 0%, #4a8 100%)",
                        borderRadius: 3,
                        display: "flex", alignItems: "center", justifyContent: "center",
                        fontSize: 18, color: "rgba(255,255,255,0.85)",
                      }}>{t.icon || "≋"}</div>
                      <span style={{ fontSize: 10, color: "var(--text-dim)", textAlign: "center",
                                     maxWidth: 78, whiteSpace: "nowrap",
                                     overflow: "hidden", textOverflow: "ellipsis" }}>
                        {t.label}
                      </span>
                    </div>
                  )
                ))}
              </div>
            </section>
          ))}
        </div>
      </div>
    </div>
  );
}
